// Extrato para Conferência — a prévia do mesmo relatório que o Microuniverso
// imprime, e o botão que manda ele em PDF pro WhatsApp do cliente.
//
// A tela mostra exatamente as colunas do PDF (Fil, Nº Documento, Tipo, Pedido,
// Vencimento, Valor, Encargos) de propósito: a crediarista confere aqui o que o
// cliente vai receber, sem precisar abrir o arquivo antes.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import { baixarArquivo } from '../helpers/appNativo';

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const dia = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

const VERDE = '#3D8B37';
const VERMELHO = '#dc3545';

const th = {
  textAlign: 'left', padding: '7px 10px', fontSize: '0.7rem', fontWeight: 700,
  color: 'var(--portal-muted)', textTransform: 'uppercase', letterSpacing: '0.04em',
  whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'var(--portal-bg)',
  borderBottom: '1px solid var(--portal-border)',
};
const thNum = { ...th, textAlign: 'right' };
const td = { padding: '6px 10px', fontSize: '0.84rem', whiteSpace: 'nowrap' };
const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const botao = (destaque) => ({
  background: destaque ? VERDE : 'transparent',
  color: destaque ? '#fff' : 'var(--portal-text)',
  border: `1px solid ${destaque ? VERDE : 'var(--portal-border)'}`,
  borderRadius: 6, padding: '6px 12px', fontSize: '0.85rem',
  cursor: 'pointer', whiteSpace: 'nowrap',
});

export default function ExtratoModal({
  aberto, oid, cliente, conversaId, onClose, onEnviado,
}) {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [extrato, setExtrato] = useState(null);
  const [ocupado, setOcupado] = useState('');

  useEffect(() => {
    if (!aberto || !oid) return undefined;
    let vivo = true;
    setCarregando(true); setErro(''); setExtrato(null);
    axios.get(`${API}/consulta-cliente/${oid}/extrato`)
      .then((r) => { if (vivo) setExtrato(r.data?.data || null); })
      .catch((e) => {
        if (vivo) setErro(e?.response?.data?.message || 'Não deu pra montar o extrato.');
      })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [aberto, oid]);

  useEffect(() => {
    if (!aberto) return undefined;
    const aoTeclar = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, onClose]);

  const baixar = useCallback(async () => {
    setOcupado('baixar'); setErro('');
    try {
      const r = await axios.get(`${API}/consulta-cliente/${oid}/extrato/pdf`, { responseType: 'blob' });
      const nome = `extrato-${String(cliente?.nome || 'cliente').toLowerCase().replace(/\s+/g, '-')}.pdf`;
      await baixarArquivo(nome, new Blob([r.data], { type: 'application/pdf' }));
    } catch (e) {
      setErro('Não deu pra gerar o PDF.');
    } finally {
      setOcupado('');
    }
  }, [oid, cliente]);

  // Mandar o PDF é irreversível: cai no WhatsApp do cliente na hora. Por isso
  // confirma mostrando o total e deixa a mensagem de acompanhamento à mão.
  const enviar = useCallback(async () => {
    const { isConfirmed, value } = await Swal.fire({
      icon: 'question',
      title: 'Enviar o extrato?',
      html: `O PDF vai pro WhatsApp desta conversa.<br>`
        + `<b>${extrato?.linhas?.length || 0}</b> títulos · total `
        + `<b>R$ ${dinheiro(extrato?.totais?.geral)}</b>`,
      input: 'text',
      inputLabel: 'Mensagem junto (opcional)',
      inputPlaceholder: 'Ex.: segue seu extrato, qualquer dúvida me chama',
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: VERDE,
    });
    if (!isConfirmed) return;
    setOcupado('enviar'); setErro('');
    try {
      await axios.post(`${API}/consulta-cliente/${oid}/extrato/enviar`, {
        conversa_id: conversaId,
        texto: value || '',
      });
      onEnviado?.();
      onClose();
      Swal.fire({ icon: 'success', title: 'Extrato enviado', timer: 1600, showConfirmButton: false });
    } catch (e) {
      setErro(e?.response?.data?.message || 'Não deu pra enviar o extrato.');
    } finally {
      setOcupado('');
    }
  }, [oid, conversaId, extrato, onClose, onEnviado]);

  if (!aberto) return null;

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.72)', zIndex: 1060 }} onClick={onClose}>
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        style={{ maxWidth: 820 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>

          <div className="modal-header" style={{ borderColor: 'var(--portal-border)' }}>
            <div style={{ minWidth: 0 }}>
              <h5 className="modal-title" style={{ fontSize: '1rem', marginBottom: 2 }}>
                <i className="bi bi-file-earmark-text me-2" />
                Extrato para conferência
              </h5>
              <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                {cliente?.nome || ''} · mesmos números e mesmo layout do Microuniverso
              </div>
            </div>
            <button type="button" onClick={onClose} title="Fechar (Esc)" style={botao(false)}>
              <i className="bi bi-x-lg me-1" /> Fechar
            </button>
          </div>

          <div className="modal-body">
            {erro && (
              <div style={{
                fontSize: '0.85rem', color: VERMELHO, border: `1px solid ${VERMELHO}`,
                borderRadius: 8, padding: '8px 10px', marginBottom: 12,
              }}>
                {erro}
              </div>
            )}

            {carregando && (
              <div style={{ fontSize: '0.85rem', color: 'var(--portal-muted)' }}>Montando o extrato…</div>
            )}

            {!carregando && extrato && extrato.linhas.length === 0 && (
              <div style={{
                border: '1px solid var(--portal-border)', borderRadius: 10,
                padding: '16px 12px', textAlign: 'center', fontSize: '0.85rem',
                color: 'var(--portal-muted)',
              }}>
                Este cliente não tem documento em aberto — não há extrato pra enviar.
              </div>
            )}

            {!carregando && extrato && extrato.linhas.length > 0 && (
              <div style={{ border: '1px solid var(--portal-border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ maxHeight: '48vh', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>Fil</th>
                        <th style={th}>Nº Documento</th>
                        <th style={th}>Tipo Documento</th>
                        <th style={th}>Pedido</th>
                        <th style={th}>Vencimento</th>
                        <th style={thNum}>Valor</th>
                        <th style={thNum}>Encargos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extrato.linhas.map((l, i) => (
                        <tr
                          key={`${l.documento}-${i}`}
                          style={{ borderTop: i ? '1px solid var(--portal-border)' : 'none' }}
                        >
                          <td style={td}>{String(l.filial || '').padStart(2, '0')}</td>
                          <td style={td}>{l.documento}</td>
                          <td style={td}>{l.tipo}</td>
                          {/* "55 pedidos" no PDF vira a lista inteira no title —
                              a crediarista confere sem abrir o arquivo. */}
                          <td style={td} title={l.pedidos?.length ? l.pedidos.join(', ') : undefined}>
                            {l.pedido}
                            {l.pedidos?.length > 0 && (
                              <i className="bi bi-info-circle ms-1" style={{ opacity: 0.5, fontSize: '0.75rem' }} />
                            )}
                          </td>
                          <td style={{ ...td, color: l.dias_atraso > 0 ? VERMELHO : undefined }}>
                            {dia(l.vencimento)}
                          </td>
                          <td style={tdNum}>{dinheiro(l.valor)}</td>
                          <td style={{ ...tdNum, color: l.encargo > 0 ? VERMELHO : undefined }}>
                            {dinheiro(l.encargo)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Os mesmos três números que abrem o PDF, pra prévia e
                    documento contarem a mesma história. */}
                <div style={{
                  borderTop: '1px solid var(--portal-border)', background: 'var(--portal-bg)',
                  padding: '8px 10px', display: 'flex', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: '4px 24px', fontSize: '0.85rem',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  <span style={{ color: 'var(--portal-muted)' }}>
                    Vencido:{' '}
                    <strong style={{ color: extrato.totais.qtd_vencido > 0 ? VERMELHO : undefined }}>
                      {dinheiro(extrato.totais.vencido)}
                    </strong>
                    {extrato.totais.qtd_vencido > 0 && ` (${extrato.totais.qtd_vencido})`}
                    {'  ·  A vencer: '}
                    <strong>{dinheiro(extrato.totais.a_vencer)}</strong>
                  </span>
                  <span>
                    Total: <strong>{dinheiro(extrato.totais.valor)}</strong>
                    {'  ·  Encargos: '}<strong>{dinheiro(extrato.totais.encargo)}</strong>
                    {'  ·  Total Geral: '}<strong>{dinheiro(extrato.totais.geral)}</strong>
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer" style={{ borderColor: 'var(--portal-border)', gap: 8 }}>
            <button
              type="button"
              onClick={baixar}
              disabled={!extrato?.linhas?.length || !!ocupado}
              style={{ ...botao(false), opacity: (!extrato?.linhas?.length || ocupado) ? 0.6 : 1 }}
            >
              <i className="bi bi-download me-1" />
              {ocupado === 'baixar' ? 'Gerando…' : 'Baixar PDF'}
            </button>
            {/* Sem conversa aberta não há pra quem mandar — só o download faz sentido. */}
            {conversaId && (
              <button
                type="button"
                onClick={enviar}
                disabled={!extrato?.linhas?.length || !!ocupado}
                style={{ ...botao(true), opacity: (!extrato?.linhas?.length || ocupado) ? 0.6 : 1 }}
              >
                <i className="bi bi-whatsapp me-1" />
                {ocupado === 'enviar' ? 'Enviando…' : 'Enviar pro cliente'}
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
